import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, worksheetApi, getMediaUrl } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/physio-dashboard.css';

const FORMAT_BADGES = {
  pdf: { label: 'PDF Document', color: '#e11d48', bg: '#ffe4e6', icon: '📄' },
  doc: { label: 'Word Doc', color: '#2563eb', bg: '#dbeafe', icon: '📝' },
  docx: { label: 'Word Docx', color: '#2563eb', bg: '#dbeafe', icon: '📝' },
  xlsx: { label: 'Excel Sheet', color: '#059669', bg: '#d1fae5', icon: '📊' },
  xls: { label: 'Excel Sheet', color: '#059669', bg: '#d1fae5', icon: '📊' },
  image: { label: 'Image / Diagram', color: '#7c3aed', bg: '#ede9fe', icon: '🖼️' },
  other: { label: 'Document', color: '#4b5563', bg: '#f3f4f6', icon: '📑' },
};

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function TherapistWorksheetsPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedWorksheet, setSelectedWorksheet] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  // Upload Modal State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [modalError, setModalError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    references: '',
    fileUrl: '',
    fileName: '',
    fileSize: 0,
    fileType: 'pdf',
    isPublished: true,
  });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4500);
  };

  const fetchWorksheets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await worksheetApi.therapistGetWorksheets({
        search: searchQuery,
        sortBy,
      });

      if (res?.success) {
        setWorksheets(res.worksheets || []);
      }
    } catch (err) {
      console.error('Failed to load therapist worksheets:', err);
      setError(err.message || 'Failed to load clinical worksheets');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, sortBy]);

  useEffect(() => {
    if (!authSession?.token || (authSession.user?.role !== 'physiotherapist' && authSession.user?.role !== 'admin')) {
      navigate('/login');
      return;
    }
    fetchWorksheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWorksheets, authSession?.token, navigate]);

  // Upload helper to upload file to backend /api/upload/file
  const uploadFileToServer = async (file) => {
    const uploadData = new FormData();
    uploadData.append('file', file);
    const res = await worksheetApi.uploadWorksheetFile(uploadData);
    if (!res || !res.url) {
      throw new Error(res?.message || 'File upload failed. Server did not return a file URL.');
    }
    const ext = file.name.split('.').pop()?.toLowerCase() || 'pdf';
    return {
      fileUrl: res.url,
      fileName: res.fileName || file.name,
      fileSize: res.fileSize || file.size,
      fileType: ext,
    };
  };

  // Process File Selection
  const processSelectedFile = async (file) => {
    if (!file) return;
    setModalError('');
    setSelectedFile(file);

    const ext = file.name.split('.').pop()?.toLowerCase() || 'pdf';
    setFormData((prev) => ({
      ...prev,
      fileName: file.name,
      fileSize: file.size,
      fileType: ext,
    }));

    try {
      setUploadingFile(true);
      const uploadResult = await uploadFileToServer(file);
      setFormData((prev) => ({
        ...prev,
        fileUrl: uploadResult.fileUrl,
        fileName: uploadResult.fileName,
        fileSize: uploadResult.fileSize,
        fileType: uploadResult.fileType,
      }));
      showFeedback('success', `File "${file.name}" uploaded successfully!`);
    } catch (err) {
      console.error('File upload error:', err);
      setModalError(`File upload warning: ${err.message || 'Could not upload automatically. Will retry upon submit.'}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processSelectedFile(file);
    }
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processSelectedFile(file);
    }
  };

  const handleOpenUploadModal = () => {
    setSelectedFile(null);
    setModalError('');
    setFormData({
      title: '',
      description: '',
      references: '',
      fileUrl: '',
      fileName: '',
      fileSize: 0,
      fileType: 'pdf',
      isPublished: true,
    });
    setIsUploadModalOpen(true);
  };

  const handleSubmitUpload = async (e) => {
    e.preventDefault();
    setModalError('');

    if (!formData.title.trim()) {
      setModalError('Please enter a worksheet title.');
      return;
    }

    let currentFileUrl = formData.fileUrl?.trim() || '';
    let currentFileName = formData.fileName || '';
    let currentFileSize = formData.fileSize || 0;
    let currentFileType = formData.fileType || 'pdf';

    try {
      setFormSubmitting(true);

      // If file was chosen but not uploaded yet, upload it now
      if (!currentFileUrl && selectedFile) {
        setUploadingFile(true);
        const uploadResult = await uploadFileToServer(selectedFile);
        currentFileUrl = uploadResult.fileUrl;
        currentFileName = uploadResult.fileName;
        currentFileSize = uploadResult.fileSize;
        currentFileType = uploadResult.fileType;
        setFormData((prev) => ({
          ...prev,
          fileUrl: currentFileUrl,
          fileName: currentFileName,
          fileSize: currentFileSize,
          fileType: currentFileType,
        }));
      }

      if (!currentFileUrl) {
        setModalError('Please choose a worksheet file from your PC or paste a document URL.');
        setFormSubmitting(false);
        setUploadingFile(false);
        return;
      }

      const payload = {
        ...formData,
        fileUrl: currentFileUrl,
        fileName: currentFileName,
        fileSize: currentFileSize,
        fileType: currentFileType,
      };

      const res = await worksheetApi.therapistCreateWorksheet(payload);
      if (res?.success) {
        showFeedback('success', 'Worksheet uploaded and shared successfully!');
        setIsUploadModalOpen(false);
        fetchWorksheets();
      } else {
        throw new Error(res?.message || 'Failed to upload worksheet');
      }
    } catch (err) {
      console.error('Worksheet upload error:', err);
      const errMsg = err.message || 'Failed to save worksheet.';
      setModalError(errMsg);
      showFeedback('error', errMsg);
    } finally {
      setFormSubmitting(false);
      setUploadingFile(false);
    }
  };

  // Handle Download Action
  const handleDownload = async (worksheet) => {
    const id = worksheet._id || worksheet.id;
    try {
      setDownloadingId(id);
      const res = await worksheetApi.therapistDownloadWorksheet(id);

      // Open or trigger download
      const targetUrl = getMediaUrl(res?.downloadUrl || worksheet.fileUrl);
      const downloadLink = document.createElement('a');
      downloadLink.href = targetUrl;
      downloadLink.target = '_blank';
      downloadLink.download = res?.fileName || worksheet.fileName || 'clinical_worksheet.pdf';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Update local download counter
      setWorksheets((prev) =>
        prev.map((w) => ((w._id === id || w.id === id) ? { ...w, downloadCount: (w.downloadCount || 0) + 1 } : w))
      );
    } catch (err) {
      console.error('Download error:', err);
      // Fallback direct open
      window.open(getMediaUrl(worksheet.fileUrl), '_blank');
    } finally {
      setDownloadingId(null);
    }
  };

  const filteredWorksheets = worksheets.filter((w) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      w.title.toLowerCase().includes(q) ||
      (w.description && w.description.toLowerCase().includes(q)) ||
      (w.references && w.references.toLowerCase().includes(q)) ||
      (w.fileName && w.fileName.toLowerCase().includes(q));
    return matchesSearch;
  });

  const getFormatBadge = (type = 'pdf') => {
    const clean = String(type).toLowerCase().replace('.', '');
    return FORMAT_BADGES[clean] || FORMAT_BADGES.other;
  };

  return (
    <div className="physio-dashboard" style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <DashboardSidebar userRole={authSession?.user?.role || 'physiotherapist'} />

      <main style={{ marginLeft: '280px', flex: 1, padding: '32px', maxWidth: '1400px' }}>
        {/* Toast Feedback Notification */}
        {feedback.message && (
          <div
            style={{
              position: 'fixed',
              top: '25px',
              right: '25px',
              zIndex: 9999,
              padding: '16px 24px',
              borderRadius: '12px',
              backgroundColor: feedback.type === 'error' ? '#ef4444' : '#10b981',
              color: 'white',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '15px',
              fontWeight: 600,
              animation: 'fadeIn 0.3s ease',
            }}
          >
            <span>{feedback.type === 'error' ? '⚠️' : '✅'}</span>
            <span>{feedback.message}</span>
            <button
              onClick={() => setFeedback({ type: '', message: '' })}
              style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '18px', marginLeft: '10px' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Page Banner Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            borderRadius: '20px',
            padding: '36px 32px',
            color: 'white',
            marginBottom: '30px',
            boxShadow: '0 10px 25px rgba(79, 70, 229, 0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px',
          }}
        >
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, marginBottom: '12px' }}>
              <span>✨</span>
              <span>PRACTITIONER RESOURCE VAULT</span>
            </div>
            <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 800, letterSpacing: '-0.5px' }}>
              Clinical Worksheets & Assessment Library
            </h1>
            <p style={{ margin: 0, fontSize: '15px', opacity: 0.9, maxWidth: '650px', lineHeight: 1.5 }}>
              Evidence-based CBT thought records, grounding tools, behavioral activation logs, and clinical guides verified by Psychmitra for use in your therapy sessions.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', padding: '16px 22px', borderRadius: '14px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 800 }}>{worksheets.length}</div>
              <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>Available Tools</div>
            </div>

            <button
              onClick={handleOpenUploadModal}
              style={{
                background: '#ffffff',
                color: '#4f46e5',
                border: 'none',
                padding: '14px 24px',
                borderRadius: '12px',
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <span>➕</span>
              <span>Upload Worksheet</span>
            </button>
          </div>
        </div>

        {/* Search & Category Pills */}
        <div style={{ background: 'white', padding: '22px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', marginBottom: '25px' }}>
          {/* Top Search & Sort Row */}
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '260px' }}>
              <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                🔍
              </span>
              <input
                type="text"
                placeholder="Search worksheets by name, clinical keyword, or topic (e.g. CBT, Anxiety, Trauma)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px 12px 42px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '12px 18px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: 'white',
                color: '#334155',
                fontWeight: 600,
                outline: 'none',
              }}
            >
              <option value="newest">⭐ Newest Releases</option>
              <option value="downloads">🔥 Most Popular with Therapists</option>
              <option value="title">🔤 Alphabetical (A-Z)</option>
            </select>
          </div>
        </div>

        {/* Worksheets Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '70px 20px', background: 'white', borderRadius: '16px' }}>
            <div style={{ fontSize: '36px', marginBottom: '14px' }}>⏳</div>
            <h3 style={{ margin: 0, color: '#334155' }}>Loading Clinical Tools...</h3>
            <p style={{ color: '#94a3b8', fontSize: '14px' }}>Fetching worksheet library</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '50px 20px', background: '#fee2e2', borderRadius: '16px', color: '#991b1b' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 8px 0' }}>Failed to load worksheets</h3>
            <p style={{ margin: 0 }}>{error}</p>
            <button
              onClick={fetchWorksheets}
              style={{ marginTop: '15px', background: '#b91c1c', color: 'white', border: 'none', padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
            >
              Try Again
            </button>
          </div>
        ) : filteredWorksheets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '48px', marginBottom: '14px' }}>🔍</div>
            <h3 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>No matching worksheets found</h3>
            <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '420px', margin: '0 auto' }}>
              No clinical worksheets matched "{searchQuery}". Try clearing your search.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
              }}
              style={{ marginTop: '18px', background: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
            >
              Clear Search
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '22px' }}>
            {filteredWorksheets.map((item) => {
              const badge = getFormatBadge(item.fileType);
              const isDownloading = downloadingId === (item._id || item.id);

              return (
                <div
                  key={item._id || item.id}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                    transition: 'all 0.25s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(79, 70, 229, 0.08)';
                    e.currentTarget.style.borderColor = '#c7d2fe';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.03)';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                >
                  <div>
                    {/* Top Row Badges */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                      <span
                        style={{
                          backgroundColor: badge.bg,
                          color: badge.color,
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        {badge.icon} {badge.label}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                      {item.title}
                    </h3>

                    {/* Description */}
                    {item.description ? (
                      <p
                        style={{
                          color: '#475569',
                          fontSize: '13px',
                          margin: '0 0 10px 0',
                          lineHeight: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.description}
                      </p>
                    ) : (
                      <p style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic', margin: '0 0 10px 0' }}>
                        No description provided
                      </p>
                    )}

                    {/* References pill if present */}
                    {item.references && (
                      <div
                        style={{
                          marginBottom: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          color: '#475569',
                          maxWidth: '100%',
                        }}
                        title={item.references}
                      >
                        <span>📚</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          Ref: {item.references}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Footer & Actions */}
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', marginTop: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '12px', marginBottom: '14px' }}>
                      <span>📦 {formatFileSize(item.fileSize)}</span>
                      <span>📥 {item.downloadCount || 0} Downloads</span>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => setSelectedWorksheet(item)}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: '#f8fafc',
                          color: '#334155',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        👁️ Guidelines & Info
                      </button>

                      <button
                        onClick={() => handleDownload(item)}
                        disabled={isDownloading}
                        style={{
                          flex: 1.2,
                          padding: '10px 14px',
                          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: 700,
                          cursor: isDownloading ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          boxShadow: '0 3px 10px rgba(79, 70, 229, 0.25)',
                        }}
                      >
                        <span>{isDownloading ? '⏳' : '📥'}</span>
                        <span>{isDownloading ? 'Downloading...' : 'Download Tool'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ==================== WORKSHEET DETAILS & CLINICAL GUIDE MODAL ==================== */}
        {selectedWorksheet && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '18px',
                width: '100%',
                maxWidth: '640px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              }}
            >
              <div
                style={{
                  padding: '22px 28px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc',
                  borderTopLeftRadius: '18px',
                  borderTopRightRadius: '18px',
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
                    {selectedWorksheet.title}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedWorksheet(null)}
                  style={{ background: 'transparent', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              <div style={{ padding: '28px' }}>
                {/* Description Box in Details Modal */}
                {selectedWorksheet.description && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 18px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px', marginBottom: '6px' }}>
                      Worksheet Description & Utility
                    </div>
                    <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: 1.6 }}>
                      {selectedWorksheet.description}
                    </p>
                  </div>
                )}

                {/* References Box in Details Modal */}
                {selectedWorksheet.references && (
                  <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '16px 18px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#4338ca', letterSpacing: '0.5px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>📚</span>
                      <span>References & Clinical Citations</span>
                    </div>
                    <p style={{ margin: 0, color: '#3730a3', fontSize: '13px', lineHeight: 1.6, fontStyle: 'italic' }}>
                      {selectedWorksheet.references}
                    </p>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', background: '#f8fafc', padding: '16px', borderRadius: '10px', marginBottom: '24px', fontSize: '13px' }}>
                  <div>
                    <span style={{ color: '#64748b' }}>File Format:</span> <strong>{selectedWorksheet.fileType?.toUpperCase() || 'PDF'}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>File Size:</span> <strong>{formatFileSize(selectedWorksheet.fileSize)}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Total Downloads:</span> <strong>{selectedWorksheet.downloadCount || 0} times</strong>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setSelectedWorksheet(null)}
                    style={{ padding: '10px 18px', background: '#f1f5f9', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      handleDownload(selectedWorksheet);
                      setSelectedWorksheet(null);
                    }}
                    style={{
                      padding: '10px 22px',
                      background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                    }}
                  >
                    <span>📥</span>
                    <span>Download Worksheet File</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== UPLOAD WORKSHEET MODAL FOR THERAPISTS ==================== */}
        {isUploadModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '18px',
                width: '100%',
                maxWidth: '680px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                animation: 'scaleUp 0.25s ease',
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  padding: '22px 28px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc',
                  borderTopLeftRadius: '18px',
                  borderTopRightRadius: '18px',
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
                    ➕ Upload New Clinical Worksheet
                  </h2>
                  <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                    Share clinical worksheets, exercises, or tools with registered therapists.
                  </p>
                </div>
                <button
                  onClick={() => setIsUploadModalOpen(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '22px',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleSubmitUpload} style={{ padding: '28px' }}>
                {modalError && (
                  <div
                    style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#991b1b',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      marginBottom: '20px',
                      fontSize: '13px',
                      lineHeight: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <span>⚠️</span>
                    <span>{modalError}</span>
                  </div>
                )}

                {/* Worksheet Name */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                    Worksheet Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter worksheet name (e.g. CBT Thought Record, Daily Mood Tracker)"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '15px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Worksheet Description */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                    Worksheet Description
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Enter a brief summary of the worksheet, clinical utility, or focus areas..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: 1.5,
                    }}
                  />
                </div>

                {/* Worksheet References */}
                <div style={{ marginBottom: '22px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                    References / Clinical Citations
                  </label>
                  <textarea
                    rows={2}
                    placeholder="e.g. Beck, A.T. (1979). Cognitive Therapy of Depression; Linehan, M.M. (2014) DBT Skills Training Manual; source DOI or book references..."
                    value={formData.references}
                    onChange={(e) => setFormData({ ...formData, references: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: 1.5,
                    }}
                  />
                  <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                    Optional: Add evidence-based literature or citations.
                  </span>
                </div>

                {/* File Upload Section */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                  }}
                  onDrop={handleDrop}
                  style={{
                    background: isDragging ? '#eef2ff' : '#f8fafc',
                    border: isDragging ? '2px dashed #6366f1' : '2px dashed #cbd5e1',
                    borderRadius: '14px',
                    padding: '28px 20px',
                    marginBottom: '25px',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontSize: '38px', marginBottom: '8px' }}>📤</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                    Upload Worksheet File *
                  </div>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px 0' }}>
                    Drag & drop or choose PDF, Word document, Excel, or Image (Max 50MB)
                  </p>

                  <input
                    type="file"
                    id="therapistWorksheetFileInput"
                    onChange={handleFileUpload}
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg"
                    style={{ display: 'none' }}
                  />

                  <label
                    htmlFor="therapistWorksheetFileInput"
                    style={{
                      display: 'inline-block',
                      background: uploadingFile ? '#94a3b8' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      color: 'white',
                      padding: '10px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: uploadingFile ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
                      transition: 'all 0.2s',
                    }}
                  >
                    {uploadingFile ? '⏳ Uploading Document...' : '📁 Choose File from PC'}
                  </label>

                  {formData.fileName && (
                    <div
                      style={{
                        marginTop: '16px',
                        padding: '10px 16px',
                        background: '#e0e7ff',
                        color: '#3730a3',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <span>✓ Ready: {formData.fileName}</span>
                      <span>({formatFileSize(formData.fileSize)})</span>
                    </div>
                  )}

                  {/* Fallback Direct URL input */}
                  <div style={{ marginTop: '18px', textAlign: 'left' }}>
                    <label style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                      Or paste File / Document URL:
                    </label>
                    <input
                      type="text"
                      placeholder="https://example.com/worksheet.pdf"
                      value={formData.fileUrl}
                      onChange={(e) => setFormData({ ...formData, fileUrl: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        marginTop: '6px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                {/* Submit Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setIsUploadModalOpen(false)}
                    style={{
                      padding: '11px 22px',
                      background: '#f1f5f9',
                      color: '#475569',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting || uploadingFile}
                    style={{
                      padding: '11px 26px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: formSubmitting || uploadingFile ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                    }}
                  >
                    {formSubmitting ? '⏳ Saving...' : '🚀 Upload Worksheet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default TherapistWorksheetsPage;
